/**
 * GanttStickyHeader — the sticky top + sticky-left frozen header for the
 * virtualised Gantt list.
 *
 * Extracted from `GanttChart` to module scope (instead of being defined
 * inside the parent component) so React never recreates the type on every
 * render. That preserves identity for `react-window`, which uses
 * `innerElementType` and remounts when the type changes — losing scroll
 * position, focus, and any cached DOM.
 *
 * The component is wrapped in `forwardRef` because react-window needs a ref
 * to the inner DOM element to measure it.
 */
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../context/I18nContext";

export const HEADER_HEIGHT = 56;       // label row + filter row
const COL_LABEL_H = 28;
const COL_FILTER_H = 28;

const GanttStickyHeader = forwardRef(function GanttStickyHeader(
  {
    children,
    style,
    // Geometry
    totalWidth,
    totalLeftWidth,
    // Columns + filters
    visibleCols,
    distinctValues,
    searchableKeys,
    // Timeline
    bounds,
    headerMarkup,
    addDays,
    // Drag handlers
    onColResize,        // (key, currentWidth) => mouseDownHandler
    onSplitResize,      // mouseDownHandler
    // From react-window
    ...rest
  },
  ref,
) {
  // react-window passes the inner element's style with `height` =
  // itemCount * itemSize. Grow it by HEADER_HEIGHT so the absolute-positioned
  // rows have somewhere to sit below the sticky header; override width so
  // horizontal scrolling stretches across the full timeline.
  const innerHeight = (parseFloat(style?.height) || 0) + HEADER_HEIGHT;
  return (
    <div ref={ref} {...rest} style={{ ...style, height: innerHeight, width: totalWidth, position: "relative" }}>
      <div
        className="sticky top-0 z-20 bg-zinc-50 border-b border-zinc-200 flex"
        style={{ height: HEADER_HEIGHT, width: totalWidth }}
        data-testid="gantt-sticky-header"
      >
        <FrozenLeftHeader
          totalLeftWidth={totalLeftWidth}
          visibleCols={visibleCols}
          distinctValues={distinctValues}
          searchableKeys={searchableKeys}
          onColResize={onColResize}
          onSplitResize={onSplitResize}
        />
        <TimelineHeader bounds={bounds} headerMarkup={headerMarkup} addDays={addDays} />
      </div>
      {children}
    </div>
  );
});

function FrozenLeftHeader({ totalLeftWidth, visibleCols, distinctValues, searchableKeys, onColResize, onSplitResize }) {
  return (
    <div
      className="sticky left-0 z-30 bg-zinc-50 flex shrink-0 border-r border-zinc-200"
      style={{ width: totalLeftWidth }}
    >
      <div className="flex flex-col" style={{ width: totalLeftWidth }}>
        <div className="flex shrink-0" style={{ height: COL_LABEL_H }}>
          {visibleCols.map((c) => <ColumnLabel key={c.key} col={c} onColResize={onColResize} />)}
        </div>
        <div className="flex shrink-0 border-b border-zinc-200" style={{ height: COL_FILTER_H }}>
          {visibleCols.map((c) => (
            <ColumnFilter
              key={c.key}
              col={c}
              isSearchable={searchableKeys.has(c.key)}
              distinct={distinctValues[c.key] || []}
            />
          ))}
        </div>
      </div>
      <div
        onMouseDown={onSplitResize}
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-cs-accent/60 z-40"
        data-testid="gantt-split-handle"
      />
    </div>
  );
}

function ColumnLabel({ col, onColResize }) {
  return (
    <div
      className="cs-label !text-[10px] px-2 flex items-center relative border-b border-zinc-200 shrink-0 select-none"
      style={{ width: col.width }}
      data-testid={`gantt-col-label-${col.key}`}
    >
      <span className="truncate">{col.label}</span>
      <div
        onMouseDown={onColResize(col.key, col.width)}
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-cs-accent/60"
        data-testid={`gantt-col-resize-${col.key}`}
      />
    </div>
  );
}

/**
 * MultiSelectFilter — dropdown filter with checkbox-based multi-selection.
 *
 * Replaces the simple <select> previously used so admins can pick multiple
 * Phases / Cost Codes / etc. at once. The selection is stored as a JS Array
 * on `col.filter` (the rest of the filtering pipeline transparently handles
 * both `string` and `string[]` for the searchable-text case).
 *
 * Closes on outside click + Esc; persists in `col.filter` so saved views
 * round-trip correctly.
 */
function MultiSelectFilter({ col, distinct }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  // Coerce current filter to a normalized Set of selected string values so we
  // can keep ALL legacy single-string filters working seamlessly.
  const selected = useMemo(() => {
    const f = col.filter;
    if (Array.isArray(f)) return new Set(f.map(String));
    if (f == null || f === "") return new Set();
    return new Set([String(f)]);
  }, [col.filter]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const commit = (next) => {
    // Persist an Array (not Set) so the value JSON-serialises into saved views.
    const arr = Array.from(next);
    col.onFilterChange?.(col.key, arr.length ? arr : "");
  };

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    commit(next);
  };
  const clear = () => commit(new Set());
  const selectAllFiltered = (visibleVals) => commit(new Set(visibleVals.map(String)));

  const filtered = q
    ? distinct.filter((v) => String(v).toLowerCase().includes(q.toLowerCase()))
    : distinct;

  const label = selected.size === 0
    ? t("filter_all")
    : selected.size === 1
      ? Array.from(selected)[0]
      : `${selected.size} ${t("filter_selected_short")}`;

  return (
    <div ref={wrapRef} className="relative px-1 shrink-0" style={{ width: col.width }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-1.5 py-0.5 text-[10px] border rounded font-mono bg-white outline-none flex items-center justify-between gap-1 ${selected.size ? "border-cs-accent text-cs-accent" : "border-zinc-200 text-zinc-700"}`}
        data-testid={`gantt-filter-${col.key}`}
        title={selected.size > 1 ? Array.from(selected).join(", ") : undefined}
      >
        <span className="truncate">{label}</span>
        <span className="opacity-50">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 left-1 top-full mt-1 w-56 bg-white border border-zinc-300 rounded-md shadow-lg p-2" data-testid={`gantt-filter-popover-${col.key}`}>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("filter_search_placeholder")}
            className="w-full px-2 py-1 mb-2 text-[11px] border border-zinc-200 rounded font-mono outline-none focus:border-cs-accent"
            autoFocus
            data-testid={`gantt-filter-search-${col.key}`}
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1.5">
            <button onClick={() => selectAllFiltered(filtered)} className="hover:text-cs-blueprint" data-testid={`gantt-filter-select-all-${col.key}`}>
              {t("filter_select_all")}
            </button>
            <button onClick={clear} className="hover:text-red-600" data-testid={`gantt-filter-clear-${col.key}`}>
              {t("filter_clear")}
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto" role="listbox">
            {filtered.slice(0, 500).map((v) => {
              const sv = String(v);
              const checked = selected.has(sv);
              return (
                <label key={sv} className="flex items-center gap-2 px-1 py-0.5 hover:bg-zinc-50 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(sv)}
                    className="rounded text-cs-accent focus:ring-cs-accent shrink-0"
                    data-testid={`gantt-filter-opt-${col.key}-${sv}`}
                  />
                  <span className="truncate font-mono">
                    {col.type === "boolean" ? (sv === "true" ? "✓" : "—") : sv}
                  </span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center text-[10px] text-zinc-400 py-3">{t("no_match")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ColumnFilter({ col, isSearchable, distinct }) {
  const { t } = useI18n();
  if (isSearchable) {
    return (
      <div className="px-1 flex items-center shrink-0" style={{ width: col.width }}>
        <input
          type="text"
          value={col.filter || ""}
          onChange={(e) => col.onFilterChange?.(col.key, e.target.value)}
          className="w-full px-1.5 py-0.5 text-[10px] border border-zinc-200 rounded font-mono bg-white focus:border-cs-accent outline-none"
          placeholder={t("filter_search_placeholder")}
          data-testid={`gantt-filter-${col.key}`}
        />
      </div>
    );
  }
  return <MultiSelectFilter col={col} distinct={distinct} />;
}

function TimelineHeader({ bounds, headerMarkup, addDays }) {
  if (!bounds || !headerMarkup) return null;
  return (
    <div className="flex flex-col shrink-0" style={{ width: bounds.timelineWidth }}>
      <div className="flex shrink-0 border-b border-zinc-200 bg-zinc-50" style={{ height: COL_LABEL_H }}>
        {headerMarkup.months.map((m) => (
          // Each month chip is a fixed-width container; the inner LABEL is
          // `position: sticky; left: 0` so it follows the horizontal scroll
          // and only stops when the next month's chip pushes it out. This
          // gives the user the "current month" indicator while panning
          // through the daily zoom — without any JS scroll listeners.
          <div
            key={m.key}
            className="cs-label !text-[10px] border-r border-zinc-200 shrink-0 select-none overflow-hidden relative"
            style={{ width: m.width }}
            data-testid={`gantt-month-${m.key}`}
          >
            <div className="sticky left-0 px-2 py-1.5 inline-block whitespace-nowrap bg-zinc-50">
              {m.label}
            </div>
          </div>
        ))}
      </div>
      <div className="flex shrink-0 border-b border-zinc-200" style={{ height: COL_FILTER_H }}>
        {bounds.colWidth >= 12 && Array.from({ length: bounds.totalDays }, (_, i) => {
          const d = addDays(bounds.min, i);
          const wd = d.getDay();
          const weekend = wd === 0 || wd === 6;
          return (
            <div
              key={i}
              className={`text-[10px] font-mono shrink-0 border-r border-zinc-100 flex items-center justify-center select-none ${weekend ? "bg-zinc-100 text-zinc-400" : "text-zinc-500"}`}
              style={{ width: bounds.colWidth }}
            >
              {d.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GanttStickyHeader;
